import {
  zodToOpenaiJsonSchema
} from "./chunk-FQFJR6TD.mjs";

// types/chatbots/chatbots.ts
import { z } from "zod";
var ButtonActionSchema = z.object({
  type: z.literal("chatbot-button"),
  id: z.string().describe(
    "The unique identifier for the action; uses the format _ACTION_#"
  ),
  triggerKeywords: z.array(z.string().describe("At most 250 characters")).describe(
    "Keywords/phrases to trigger the action; defaults to an empty array"
  ),
  triggerCondition: z.literal("after_every_chatbot_response"),
  config: z.object({
    buttonText: z.string().describe("At most 30 characters"),
    notificationText: z.string().describe(
      "The text to display in the notification when the button is clicked; at most 50 characters; defaults to 'Action triggered!'"
    )
  })
});
var CollectInfoActionSchema = z.object({
  type: z.literal("collect-info"),
  id: z.string().describe(
    "The unique identifier for the action; uses the format _ACTION_#"
  ),
  triggerKeywords: z.array(z.string().describe("At most 250 characters")).describe(
    "Keywords/phrases to trigger the action; defaults to an empty array"
  ),
  triggerCondition: z.union([
    z.literal("beginning_of_conversation"),
    z.literal("after_several_messages"),
    z.literal("after_keyword")
  ]),
  config: z.object({
    collectUserData: z.boolean().describe(
      "Whether to collect user data - if no appropriate table is outlined, set this to false"
    ),
    collectUserDataTableId: z.string().describe(
      "Zapier Table id to store collected user data in; Make sure to reference a table that exists in the supplied outline use '${{ tables._TABLE_x }}'. If no table exists in the outline, set collectUserDataTableId to empty string."
    ),
    collectUserDataFields: z.array(
      z.object({
        name: z.string().describe(
          "The friendly, human-readable (title cased) name of the Zapier Table field to collect from the user; at most 255 characters"
        ),
        fieldKey: z.string().nullable().describe(
          "The key to use for the field in the Zapier Table; should be in the format f# where # corresponds to the 1-based index of the matched-by-name field in the Zapier Table"
        ),
        type: z.enum([
          "string",
          "text",
          "email",
          "phone_number",
          "number",
          "link",
          "currency",
          "datetime"
        ]).describe("The format of the information to collect from the user")
      })
    ).describe(
      "The fields to collect from the user; defaults to collecting Name, Email, Phone, and Company"
    )
  })
});
var RunZapActionSchema = z.object({
  type: z.literal("RunZap"),
  id: z.string().describe(
    "The unique identifier for the action; uses the format _ACTION_#"
  ),
  triggerKeywords: z.array(z.string().describe("At most 250 characters")).describe(
    "Keywords/phrases to trigger the action; defaults to an empty array"
  ),
  triggerCondition: z.literal("end_of_conversation"),
  config: z.object({})
});
var LinkButtonActionSchema = z.object({
  type: z.literal("ScheduleButton"),
  id: z.string().describe(
    "The unique identifier for the action; uses the format _ACTION_#"
  ),
  triggerKeywords: z.array(z.string().describe("At most 250 characters")).describe(
    "Keywords/phrases to trigger the action; defaults to an empty array"
  ),
  triggerCondition: z.union([
    z.literal("after_every_chatbot_response"),
    z.literal("after_keyword")
  ]),
  config: z.object({
    buttonIconName: z.string().describe(
      "The name of the icon to display on the button from the lucide-react icon library; defaults to 'CalendarDays'"
    ),
    buttonLink: z.string().describe("The link to navigate to when the button is clicked"),
    buttonText: z.string().describe("The text to display on the button; at most 30 characters")
  })
});
var SuggestionsActionSchema = z.object({
  type: z.literal("suggestions"),
  id: z.string().describe(
    "The unique identifier for the action; uses the format _ACTION_#"
  ),
  triggerKeywords: z.array(z.string().describe("At most 250 characters")).describe(
    "Keywords/phrases to trigger the action; defaults to an empty array"
  ),
  triggerCondition: z.union([
    z.literal("beginning_of_conversation"),
    z.literal("after_every_chatbot_response"),
    z.literal("after_keyword")
  ]),
  config: z.object({
    suggestions: z.array(
      z.object({
        message: z.string().describe(
          "The message to display as the suggestion; at most 150 characters"
        )
      })
    ).describe(
      "The list of suggestions to display; defaults to an empty array"
    ),
    enableAiSuggestions: z.boolean().describe("Whether to enable AI suggestions; defaults to false")
  })
});
var FileKnowledgeSourceSchema = z.object({
  type: z.literal("file"),
  name: z.string().describe("The name of the file"),
  reference: z.string().describe("File path to the file's storage location")
});
var TableKnowledgeSourceSchema = z.object({
  type: z.literal("table"),
  name: z.string().describe(
    "Name of the Zapier Table; To reference tables that exist in the new outline use '${{ tables._TABLE_x }}'."
  ),
  reference: z.string().describe("Zapier Table id in ULID format")
});
var WebpageKnowledgeSourceSchema = z.object({
  type: z.literal("webpage"),
  name: z.string().describe("The origin portion of the URL"),
  reference: z.string().describe("URL of the webpage; Example: https://www.zapier.com")
});
var TemplateChatbotSchema = z.object({
  id: z.string(),
  chatbotName: z.string().describe("At most 150 characters").transform((val) => val.slice(0, 150).trim()),
  defaultMessageMode: z.enum(["static", "generated"]).describe(
    "Determines how the initial greeting message in the conversation is created; default to 'static'"
  ),
  defaultPrompt: z.string().describe(
    "LLM-friendly instructions for the chatbot to use in generating the initial greeting message; default to 'Say hello and tell me what you can do'"
  ),
  defaultMessage: z.string().describe(
    "The initial static greeting message in the conversation; default to 'How can I help you today?'"
  ),
  placeholder: z.string().describe(
    "The placeholder text for the chatbot; default to 'Ask me anything'"
  ),
  directive: z.string().describe(
    "LLM instructions for how the chatbot should operate; default to 'You are an AI chatbot. You are a helpful assistant.'"
  ),
  knowledgeSources: z.array(
    z.discriminatedUnion("type", [
      FileKnowledgeSourceSchema,
      TableKnowledgeSourceSchema,
      WebpageKnowledgeSourceSchema
    ])
  ).describe(
    "An array of knowledge sources for the chatbot; default to an empty array. You can only use 'table' if the outline creates a table to use for this."
  ),
  triggers: z.array(
    z.discriminatedUnion("type", [
      ButtonActionSchema,
      CollectInfoActionSchema,
      RunZapActionSchema,
      LinkButtonActionSchema,
      SuggestionsActionSchema
    ])
  ).describe(
    "An array of actions for the chatbot. Only include actions that are relevant to the outline of the system you're building. If no actions are relevant, default this to an empty array."
  )
});
var chatbotOpenAiJsonSchema = zodToOpenaiJsonSchema(
  TemplateChatbotSchema,
  "chatbots",
  {}
);

export {
  TemplateChatbotSchema,
  chatbotOpenAiJsonSchema
};
